import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { DateService } from './date.service';
import { API_CONFIG } from '../constants/api.constants';
import { 
  CallCenterStatsData, 
  CallLogRequest, 
  UpdatePlanRequest, 
  UpdateNotesRequest,
  UpdateActualRequest 
} from '../interfaces/call-center-stats.interface';

@Injectable({
  providedIn: 'root'
})
export class CallCenterStatsService {
  private apiUrl = `${API_CONFIG.BASE_URL}/call-center-stats`;
  private dateService = inject(DateService);

  constructor(private http: HttpClient) {}

  /**
   * Get HTTP headers with debug date if set
   */
  private getHeaders(): HttpHeaders {
    let headers = new HttpHeaders();
    
    const debugDate = this.dateService.debugDate();
    if (debugDate) {
      headers = headers.set('X-Debug-Date', debugDate.toISOString());
    }
    
    return headers;
  }

  /**
   * Hívás státuszának rögzítése
   */
  logCall(request: CallLogRequest): Observable<CallCenterStatsData> {
    return this.http.post<CallCenterStatsData>(`${this.apiUrl}/log-call`, request, {
      headers: this.getHeaders()
    });
  }

  /**
   * Plan értékek frissítése az Indított hívás és Egyeztetett időpont metrikákhoz
   */
  updatePlanValues(request: UpdatePlanRequest): Observable<CallCenterStatsData> {
    return this.http.put<CallCenterStatsData>(`${this.apiUrl}/update-plan`, request, {
      headers: this.getHeaders()
    });
  }

  /**
   * Felhasználó aktuális heti statisztikáinak lekérdezése
   */
  getUserCurrentWeekStats(userId: number): Observable<CallCenterStatsData> {
    return this.http.get<CallCenterStatsData>(`${this.apiUrl}/user/${userId}/current-week`, {
      headers: this.getHeaders()
    });
  }

  /**
   * Felhasználó megadott heti statisztikáinak lekérdezése
   */
  getUserWeekStats(userId: number, weekNumber: number, year: number): Observable<CallCenterStatsData> {
    const url = `${this.apiUrl}/user/${userId}/week/${weekNumber}/year/${year}`;
    
    return this.http.get<CallCenterStatsData>(url, {
      headers: this.getHeaders()
    }).pipe(
      catchError(error => {
        throw error;
      })
    );
  }

  /**
   * Felhasználó heti statisztikáinak lekérdezése dátum alapján
   */
  getUserWeekStatsByDate(userId: number, startDate: Date, endDate: Date): Observable<CallCenterStatsData> {
    // Konvertáljuk a dátumokat hét számmá és évre
    const weekInfo = this.getWeekNumberFromDate(startDate);
    return this.getUserWeekStats(userId, weekInfo.weekNumber, weekInfo.year);
  }

  /**
   * Felhasználó egy hónap összes heti statisztikáinak lekérdezése
   * @param userId - Felhasználó ID
   * @param month - Hónap (1-12) 
   * @param year - Év
   */
  getMonthStats(userId: number, month: number, year: number): Observable<CallCenterStatsData[]> {
    // Meghatározzuk a hónap heteit
    const weekNumbers = this.getWeekNumbersForMonth(month, year);
    
    // Párhuzamosan lekérjük az összes hét adatait
    const weekRequests = weekNumbers.map(weekNumber => 
      this.getUserWeekStats(userId, weekNumber, year)
    );

    return forkJoin(weekRequests);
  }

  /**
   * Több felhasználó, több hét statisztikáinak egyszerre történő lekérdezése (bulk)
   * Egyetlen HTTP kérés a szerver felé, egyetlen SQL query az adatbázisban
   */
  getBulkStats(userIds: number[], weekNumbers: number[], year: number): Observable<CallCenterStatsData[]> {
    return this.http.post<CallCenterStatsData[]>(`${this.apiUrl}/bulk`, {
      user_ids: userIds,
      week_numbers: weekNumbers,
      year
    }, {
      headers: this.getHeaders()
    });
  }

  /**
   * A kiválasztott hét összes táblázatának exportálása egy XLSX fájlba
   */
  exportWeekStats(userIds: number[], weekNumber: number, year: number): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/export`, {
      user_ids: userIds,
      week_number: weekNumber,
      year
    }, {
      headers: this.getHeaders(),
      responseType: 'blob'
    });
  }

  /**
   * Havi statisztikák lekérdezése több felhasználóhoz egyetlen kéréssel
   * @returns Objektum userId kulccsal és a felhasználó heti statisztikái tömbjével
   */
  getMonthStatsBulk(userIds: number[], month: number, year: number): Observable<{ [userId: number]: CallCenterStatsData[] }> {
    const weekNumbers = this.getWeekNumbersForMonth(month, year);
    return this.getBulkStats(userIds, weekNumbers, year).pipe(
      map(allStats => {
        const grouped: { [userId: number]: CallCenterStatsData[] } = {};
        userIds.forEach(id => grouped[id] = []);
        allStats.forEach(stat => {
          if (grouped[stat.user_id]) {
            grouped[stat.user_id].push(stat);
          }
        });
        return grouped;
      })
    );
  }

  /**
   * Meghatározza egy adott hónap heteinek számait
   */
  getWeekNumbersForMonth(month: number, year: number): number[] {
    const weekNumbers: Set<number> = new Set();
    
    // Hónap első napja
    const firstDay = new Date(year, month - 1, 1);
    // Hónap utolsó napja
    const lastDay = new Date(year, month, 0);
    
    // Végigmegyünk a hónap összes napján
    for (let date = new Date(firstDay); date <= lastDay; date.setDate(date.getDate() + 1)) {
      const weekInfo = this.getWeekNumberFromDate(date);
      weekNumbers.add(weekInfo.weekNumber);
    }
    
    return Array.from(weekNumbers).sort((a, b) => a - b);
  }

  /**
   * Hét szám kinyerése dátumból
   */
  private getWeekNumberFromDate(date: Date): { weekNumber: number; year: number } {
    const year = date.getFullYear();
    
    // ISO week számítás
    const tempDate = new Date(date.getTime());
    tempDate.setHours(0, 0, 0, 0);
    tempDate.setDate(tempDate.getDate() + 3 - (tempDate.getDay() + 6) % 7);
    const week1 = new Date(tempDate.getFullYear(), 0, 4);
    const weekNumber = Math.round(((tempDate.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7) + 1;
    
    return { weekNumber: Math.max(1, Math.min(52, weekNumber)), year };
  }

  /**
   * Aktuális hét számának meghatározása
   */
  getCurrentWeekInfo(): { weekNumber: number; year: number } {
    // Debug dátum használata ha van, különben aktuális dátum
    const now = this.dateService.getCurrentDate();
    const year = now.getFullYear();
    
    // ISO week számítás
    const date = new Date(now.getTime());
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
    const week1 = new Date(date.getFullYear(), 0, 4);
    const weekNumber = Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7) + 1;
    
    return { weekNumber, year };
  }

  /**
   * Nap index meghatározása (0=Hétfő, 4=Péntek)
   */
  getCurrentDayIndex(): number {
    // Debug dátum használata ha van, különben aktuális dátum
    const now = this.dateService.getCurrentDate();
    const dayOfWeek = now.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
    
    // Konvertálás: 0=Monday, 1=Tuesday, ..., 4=Friday
    let dayIndex: number;
    if (dayOfWeek === 0) {
      dayIndex = 4; // Vasárnap -> Péntek (utolsó munkanap)
    } else if (dayOfWeek === 6) {
      dayIndex = 4; // Szombat -> Péntek (utolsó munkanap)
    } else {
      dayIndex = dayOfWeek - 1; // Hétfő-Péntek: 1-5 -> 0-4
    }
    
    return dayIndex;
  }

  /**
   * Notes mentése az adatbázisba
   */
  updateNotes(request: UpdateNotesRequest): Observable<CallCenterStatsData> {
    const headers = this.getHeaders();
    
    return this.http.post<CallCenterStatsData>(`${this.apiUrl}/update-notes`, request, { headers }).pipe(
      catchError((error: any) => {
        throw error;
      })
    );
  }

  /**
   * Actual értékek egyedi mentése (target_person_reached, listened_completely)
   */
  updateActualValue(request: UpdateActualRequest): Observable<CallCenterStatsData> {
    const headers = this.getHeaders();
    
    return this.http.post<CallCenterStatsData>(`${this.apiUrl}/update-actual`, request, { headers }).pipe(
      catchError((error: any) => {
        throw error;
      })
    );
  }

  /**
   * Lekérdezi azokat a felhasználókat, akiknek van adatuk a call-center-stats táblában egy adott hónap/év kombinációban
   */
  getUsersWithHistoricalData(year: number, month: number): Observable<number[]> {
    const headers = this.getHeaders();
    const params = { year: year.toString(), month: month.toString() };
    
    return this.http.get<{user_ids: number[]}>(`${this.apiUrl}/users-with-data`, { 
      headers, 
      params 
    }).pipe(
      map((response: {user_ids: number[]}) => response.user_ids),
      catchError((error: any) => {
        return of([]);
      })
    );
  }
}
